import { Close, SearchOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AccountAPI, OrganizationAPI } from "../../../../../api";
import AddressAPI from "../../../../../api/address";
import ContactAPI from "../../../../../api/contactInfo";
import SearchDirectory from "../../../../../components/core/GlobalSearch/SearchDirectory";
import { SubmitButton } from "../../../../../components/elements";
import { useAlert } from "../../../../../lib/alert";
import { Account, Organization } from "../../../../../model";
import {
  AccountSearchOption,
  ESAccount,
  ESMember,
  ESOrganization,
} from "../../../../../model/AccountSearchOption";
import {
  Address,
  OrganizationAddressLabels,
} from "../../../../../model/Address";
import { ContactLabels, ContactType } from "../../../../../model/Contact";
import { OrganizationMember } from "../../../../../model/User";
import { AccountCreateSimpleForm } from "../../../../accounts/components/AccountCreateSimpleForm";
import useAccountExecution from "../../../../accounts/hooks/useAccountExecution";
import { getAccountName } from "../../../../accounts/utils";
import {
  TaskCreateAccount,
  TaskCreateAccountExecution,
  TaskExecutionComponentProps,
} from "../../../types";

const OptionSelectedCard = ({
  selected,
  setSelected,
  disabled,
}: {
  selected: AccountSearchOption | undefined;
  setSelected: React.Dispatch<
    React.SetStateAction<AccountSearchOption | undefined>
  >;
  disabled?: boolean;
}) => {
  const [address, setAddress] = useState<Address | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [emailLoaded, setEmailLoaded] = useState(false);
  const [organization, setOrganization] = useState<Organization | undefined>(
    undefined,
  );
  const [selectedOrganization, setSelectedOrganization] = useState<
    ESOrganization | undefined
  >();
  const alert = useAlert();

  useEffect(() => {
    if (!selected) return undefined;
    if (selected.type === "account") {
      setEmail((selected.data as ESAccount)?.primary_email);
      setEmailLoaded(true);
    } else if (selected.type === "organization") {
      setEmail((selected.data as ESOrganization)?.email);
      setEmailLoaded(true);
    } else if (selected.type === "member") {
      const member = selected.data as ESMember;
      ContactAPI.findOneByDetailsGlobal(
        member.organizationId,
        ContactLabels.PRIMARY,
        ContactType.EMAIL,
        member.user_id,
      )
        .then((found) => {
          setEmail(found?.value ?? undefined);
          setEmailLoaded(true);
        })
        .catch((e) => {
          alert.error("Unable to fetch email for account.");
          console.error("The above error has the following details: ", e);
          setEmail(undefined);
          setEmailLoaded(true);
        });
    }
  }, [alert, selected]);

  const organizationId = useMemo(() => {
    setOrganization(undefined);
    setSelectedOrganization(undefined);
    if (!selected) return undefined;
    if (selected.type === "account") {
      const accountId = (selected.data as Account)?.id;

      if (!accountId) return;

      AccountAPI.get(accountId)
        .then((fullAccount) => {
          const orgId = fullAccount.external_org_id;
          if (orgId) {
            return OrganizationAPI.get(orgId);
          }
          throw new Error("No org ID found");
        })
        .then((org) => setOrganization(org))
        .catch(() => {
          setOrganization(undefined);
          setSelectedOrganization(undefined);
        });
    } else if (selected.type === "member") {
      return (selected.data as OrganizationMember)?.organizationId;
    }
    return undefined;
  }, [selected]);

  const [logo, setLogo] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchLogo = async () => {
      if (selected?.type === "organization") {
        setLogo((selected.data as ESOrganization)?.logo);
      } else if (selected?.type === "member") {
        const org = await OrganizationAPI.get(
          (selected.data as OrganizationMember).organizationId,
        );
        setLogo(org?.theme?.shortLogoUrl);
      } else if (selectedOrganization?.logo) {
        setLogo(selectedOrganization.logo);
      } else if (
        selected?.type === "account" &&
        (selected.data as ESAccount).external_org_id
      ) {
        const orgId = (selected.data as ESAccount).external_org_id;
        if (orgId) {
          const org = await OrganizationAPI.get(orgId);
          setLogo(org?.theme?.shortLogoUrl);
        }
      } else {
        setLogo(undefined);
      }
    };

    fetchLogo();
  }, [selected, selectedOrganization]);

  useEffect(() => {
    if (organizationId) {
      OrganizationAPI.get(organizationId)
        .then((org) => setOrganization(org))
        .catch(() => {
          setOrganization(undefined);
          setSelectedOrganization(undefined);
        });
    } else if (selected?.type === "organization") {
      setSelectedOrganization(selected.data as ESOrganization);
    } else {
      setOrganization(undefined);
      setSelectedOrganization(undefined);
    }
  }, [organizationId, selected]);

  useEffect(() => {
    setAddress(undefined);
    if (selectedOrganization?.id) {
      AddressAPI.getOrganizationAddressByLabel(
        selectedOrganization.id,
        OrganizationAddressLabels.PRIMARY,
      ).then((addr) => setAddress(addr ?? undefined));
    } else if (organization?.id) {
      AddressAPI.getOrganizationAddressByLabel(
        organization.id,
        OrganizationAddressLabels.PRIMARY,
      ).then((addr) => setAddress(addr ?? undefined));
    } else {
      setAddress(undefined);
    }
  }, [organization, selectedOrganization, selected]);

  return (
    <>
      <Container disableGutters sx={{ mb: "1rem", height: "6rem" }}>
        <Paper
          elevation={0}
          sx={{
            height: "6rem",
            p: 2,
            backgroundColor: "rgba(0, 0, 0, 0.03)",
            borderRadius: "7px",
            boxShadow: "0 2px 1px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              width: "100%",
              height: "100%",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                width: "100%",
                height: "100%",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                {logo && (
                  <img
                    src={logo}
                    style={{ width: "5rem", marginRight: "1rem" }}
                  />
                )}
                <Box>
                  <Typography variant="inherit">{selected?.name}</Typography>
                  <Typography variant="body2" style={{ color: "grey" }}>
                    {selected?.name_secondary}
                  </Typography>
                  {emailLoaded && email && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: (theme) => theme.palette.grey[600],
                      }}
                    >
                      <Link to={`mailto:${email}`} style={{ color: "inherit" }}>
                        {email}
                      </Link>
                    </Typography>
                  )}
                </Box>
              </Box>
              {address && (
                <Box minWidth={110} sx={{ ml: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: (theme) => theme.palette.grey[600],
                    }}
                  >
                    {address?.first_line}
                  </Typography>
                  {address?.second_line && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: (theme) => theme.palette.grey[600],
                      }}
                    >
                      {address?.second_line}
                    </Typography>
                  )}
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: (theme) => theme.palette.grey[600],
                    }}
                  >
                    {`${address?.city}, ${address?.state} ${address?.zip_code}`}
                  </Typography>
                </Box>
              )}
              <IconButton
                onClick={() => setSelected(undefined)}
                size="small"
                disabled={disabled}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  alignSelf: "center",
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Container>
      {emailLoaded && !email && (
        <Alert severity="warning" sx={{ mb: "1rem" }}>
          There is no email associated with this account, and they will not be
          notified.
        </Alert>
      )}
    </>
  );
};

const SearchComponent = ({
  query,
  setQuery,
  selected,
  setSelected,
  disabled,
  loading,
}: {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  selected: AccountSearchOption | undefined;
  setSelected: React.Dispatch<
    React.SetStateAction<AccountSearchOption | undefined>
  >;
  disabled?: boolean;
  loading?: boolean;
}) => {
  return selected && selected.name !== undefined ? (
    <OptionSelectedCard
      selected={selected}
      setSelected={setSelected}
      disabled={disabled}
    />
  ) : (
    <TextField
      placeholder="Search Directory"
      fullWidth
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      disabled={disabled}
      sx={{ mb: "1rem" }}
      InputProps={{
        startAdornment: <SearchOutlined sx={{ mr: 1 }} />,

        endAdornment:
          query && loading ? (
            <IconButton>
              <CircularProgress
                size={20}
                sx={{
                  color: (theme) => theme.palette.grey[600],
                  //position: "absolute",
                  //right: 10,
                }}
              />
            </IconButton>
          ) : query ? (
            <Tooltip title="Clear Search">
              <IconButton onClick={() => setQuery("")} disabled={disabled}>
                <Close />
              </IconButton>
            </Tooltip>
          ) : null,
      }}
    />
  );
};

const WrapperComponent = ({
  children,
  selected,
  props,
}: {
  children: React.ReactNode;
  selected?: AccountSearchOption;
  props: TaskExecutionComponentProps<
    TaskCreateAccount,
    TaskCreateAccountExecution
  >;
}) => {
  const { onExecute, disabled, actionBarVisible, task, taskExecution } =
    useAccountExecution(props);

  const isDisabled = disabled || taskExecution?.completed_at != null;

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: "300px",
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
      }}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        {task.name}
      </Typography>
      {children}
      {actionBarVisible && (
        <SubmitButton
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          disabled={!selected || isDisabled}
          onSubmit={() => onExecute(selected)}
        >
          Continue
        </SubmitButton>
      )}
    </Box>
  );
};

const EmptyListComponent = ({
  query,
  setQuery,
  setSelected,
  emptyDirectory,
  props,
}: {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  setSelected: React.Dispatch<
    React.SetStateAction<AccountSearchOption | undefined>
  >;
  emptyDirectory: boolean;
  props: TaskExecutionComponentProps<
    TaskCreateAccount,
    TaskCreateAccountExecution
  >;
}) => {
  const {
    accountType,
    organization,
    allAccounts,
    findAccount,
    addAccounts,
    executeAfterCreated,
    automaticallyExecute,
  } = useAccountExecution(props);
  return (
    <AccountCreateSimpleForm
      accountType={accountType}
      organizationId={organization!.id}
      account={{
        account_name: query?.trim(),
        primary_email: query && query.indexOf("@") > 0 ? query?.trim() : "",
      }}
      onClose={() => {}}
      onCreate={(account) => {
        const existing = findAccount(account);
        if (!existing) {
          setQuery(getAccountName(account));
          addAccounts([account]);
        }
        setSelected({
          type: "account",
          data: account,
          name: getAccountName(account),
        });
        if (automaticallyExecute) executeAfterCreated(account);
      }}
      setSelected={setSelected}
      query={query}
      setQuery={setQuery}
      allAccounts={allAccounts}
      isEmpty={emptyDirectory}
    />
  );
};

const AccountSearchDirectory = (
  props: TaskExecutionComponentProps<
    TaskCreateAccount,
    TaskCreateAccountExecution
  >,
) => {
  const { selected, setSelected, disabled, fetchAccounts, results, loading } =
    useAccountExecution(props);

  const RenderWrapperComponent = useCallback(
    (wrapperProps: {
      children: React.ReactNode;
      selected?: AccountSearchOption;
    }) => <WrapperComponent {...wrapperProps} props={props} />,
    [props],
  );

  const RenderEmptyListComponent = useCallback(
    (emptyProps: {
      query: string;
      setQuery: React.Dispatch<React.SetStateAction<string>>;
      setSelected: React.Dispatch<
        React.SetStateAction<AccountSearchOption | undefined>
      >;
      emptyDirectory: boolean;
    }) => (
      <EmptyListComponent
        {...emptyProps}
        emptyDirectory={results.length === 0}
        props={props}
      />
    ),
    [results.length, props],
  );

  return (
    <SearchDirectory<AccountSearchOption>
      options={results}
      setOptions={fetchAccounts}
      SearchComponent={SearchComponent}
      EmptyListComponent={RenderEmptyListComponent}
      WrapperComponent={RenderWrapperComponent}
      globalSelected={selected}
      setGlobalSelected={setSelected}
      disabled={disabled}
      loading={loading}
    />
  );
};

export default AccountSearchDirectory;
